import { useQuery } from '@tanstack/react-query';
import {
  getTasks,
  getTodayTasks,
  getOverdueTasks,
  getTask,
  getClientTasks,
} from '../services/tasksApi';
import type { TaskFilters } from '../types/task';

// Query keys for caching
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,
  today: () => [...taskKeys.all, 'today'] as const,
  overdue: () => [...taskKeys.all, 'overdue'] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  client: (clientId: string) => [...taskKeys.all, 'client', clientId] as const,
};

/**
 * Hook to fetch tasks with filters and pagination
 */
export const useTasks = (filters?: TaskFilters) => {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => getTasks(filters),
  });
};

/**
 * Hook to fetch today's tasks
 */
export const useTodayTasks = () => {
  return useQuery({
    queryKey: taskKeys.today(),
    queryFn: getTodayTasks,
  });
};

/**
 * Hook to fetch overdue tasks
 */
export const useOverdueTasks = () => {
  return useQuery({
    queryKey: taskKeys.overdue(),
    queryFn: getOverdueTasks,
  });
};

/**
 * Hook to fetch a single task
 */
export const useTask = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => getTask(id),
    enabled: options?.enabled ?? !!id,
  });
};

/**
 * Hook to fetch tasks for a specific client
 */
export const useClientTasks = (clientId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: taskKeys.client(clientId),
    queryFn: () => getClientTasks(clientId),
    enabled: options?.enabled ?? !!clientId,
  });
};

// Default export
export default useTasks;
